import React, { useState } from "react";
import { Card } from "antd";
import { useLiveSiteData } from "../hooks/useLiveSiteData";
import { CANVAS_CARD_BODY_HEIGHT } from "../../FloorPlanModal/utils/constants";
import LiveSiteCanvas from "./LiveSiteCanvas";

const CENTRED = Object.freeze({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

/**
 * Standalone "即時位置追蹤" panel — the real-time tunnel/floor-plan tracking
 * canvas, self-contained and driven only by `machineID`.
 *
 * The live dashboard's canvas for other dashboards: the same shared
 * LiveSiteCanvas (people, stations, symbols, machines, tripwires, the 圖層
 * rows, the 場外 status), fed by the same useLiveSiteData hook, which it runs
 * itself so the host only needs to pass a machineID. Only the frame differs.
 *
 * @param {string}  machineID          Required. Machine/device id to track.
 * @param {boolean} showCard           Wrap the canvas in the antd Card (with
 *                                     its title); false → frameless. 地圖
 *                                     starts off either way, so nothing
 *                                     map-related loads until someone switches
 *                                     it on.
 * @param {number}  [height]           Optional. Carded → Card body height
 *                                     (default: viewport-relative, see
 *                                     CANVAS_CARD_BODY_HEIGHT). Frameless → a minHeight
 *                                     floor; omit to size to the canvas content.
 * @param {string}  title              Card title (only when showCard), with
 *                                     the 場外 status right of it.
 * @param {boolean} isPaused           Pause live polling.
 * @param {string}  selectedPersonnelId Controlled selection (optional).
 * @param {Function} onPersonnelClick  Called with personnel id on marker click.
 * @param {Function} [renderFrame]     Frameless only: (withStatus, body) =>
 *                                     the host's own card around `body`;
 *                                     withStatus(title) appends the 場外
 *                                     status to the host's title.
 */
export const LivePositionTracker = ({
  machineID,
  showCard = true,
  height,
  title = "即時位置追蹤",
  isPaused = false,
  selectedPersonnelId: controlledSelectedId = null,
  onPersonnelClick = null,
  renderFrame = null,
}) => {
  const [internalSelectedId, setInternalSelectedId] =
    useState(controlledSelectedId);
  const selectedPersonnelId = controlledSelectedId ?? internalSelectedId;

  // The dashboard's person click selects in its personnel panel; the widget
  // has no panel, so it keeps its own selection and reports the click.
  const handlePersonnelClick = (personnelId) => {
    setInternalSelectedId(personnelId);
    if (onPersonnelClick) {
      onPersonnelClick(personnelId);
    }
  };

  const data = useLiveSiteData(machineID, { isPaused });
  // The flex centring is ac46189's (即時位置追蹤圖在儀表板置中顯示): it
  // centres the canvas within a dashboard tile that may be larger than it,
  // while the plan stays anchored at metre (0,0) inside the canvas.
  const site = (frame) => (
    <LiveSiteCanvas
      machineID={machineID}
      data={data}
      selectedPersonnelId={selectedPersonnelId}
      onPersonnelClick={handlePersonnelClick}
      containerStyle={CENTRED}
      frame={frame}
    />
  );

  if (!showCard) {
    // Frameless, used by DashboardView's `live_position` chart inside a
    // fixed-height tile. `height: 100%` is what makes the canvas able to size
    // itself: useCanvasSize measures the container's offsetHeight, and the
    // element it measures is a child of THIS wrapper, so without a height here
    // that chain resolves to `auto` -- the wrapper hugs its content, which is
    // the canvas, which is sized from the measurement. A host that gives this
    // no bounded height gets `auto` and the old content-hugging behaviour, so
    // this can only add information, never take it away.
    //
    // `height` (the prop) still acts as a minHeight floor when given.
    return site((withStatus, canvas) => {
      const body = (
        <div
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            ...(height != null ? { minHeight: height } : {}),
          }}
        >
          {canvas}
        </div>
      );
      return renderFrame ? renderFrame(withStatus, body) : body;
    });
  }

  return site((withStatus, canvas) => (
    <Card
      title={withStatus(title)}
      size="small"
      bordered
      bodyStyle={{
        padding: 12,
        position: "relative",
        height: height ?? CANVAS_CARD_BODY_HEIGHT,
      }}
    >
      {canvas}
    </Card>
  ));
};

export default LivePositionTracker;
